import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"

/**
 * Tabs root component that controls the tabs state.
 * Use with TabsList, TabsTrigger, and TabsContent.
 */
const Tabs = TabsPrimitive.Root

/**
 * TabsList component displays the container for tab triggers.
 * Built on Radix UI Tabs primitive with styled background.
 * 
 * @component
 * @param {React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>} props - List props
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Container for tab triggers
 */
const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground",
      className
    )}
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

/**
 * TabsTrigger component displays a clickable tab button.
 * Shows active state styling when selected. Built on Radix UI Tabs primitive.
 * 
 * @component
 * @param {React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>} props - Trigger props
 * @param {string} props.value - Unique value for this tab
 * @param {React.ReactNode} props.children - Tab label content
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Clickable tab trigger button
 */
const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow",
      className
    )}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

/**
 * TabsContent component displays the content panel for a tab.
 * Only visible when the corresponding tab is active. Built on Radix UI Tabs primitive.
 * 
 * @component
 * @param {React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>} props - Content props
 * @param {string} props.value - Value matching the corresponding TabsTrigger
 * @param {React.ReactNode} props.children - Content to display when tab is active
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Tab content panel
 */
const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
